// The recipe card a cook opens from the Kitchen Display or Kitchen Production
// (kitchen flow, migration 148): the ingredients and the numbered method of a
// dish or a prep item, scaled to the amount being made.
//
// One component for both screens. It owns a small back stack, so an
// ingredient that is itself made in the kitchen (a gravy inside a curry) opens
// its own card at the amount the parent needs, and Back returns to the parent
// exactly as it was left. Scaling is done here, in the browser
// (lib/recipeCard/recipeScale.ts) — changing the amount never waits on the
// network. No price or cost is shown or received.
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ChefHat, ChevronRight, Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { RECIPE_CARD_QK } from '@/hooks/useRecipeCards'
import { RC } from '@/lib/recipeCard/recipeCardStrings'
import type { RecipeCard, RecipeCardTarget } from '@/lib/recipeCard/recipeCardTypes'
import {
  SCALE_CHIPS,
  amountInMode,
  amountUnit,
  batchSummary,
  canScaleByWeight,
  chipAmount,
  factorFor,
  formatQty,
  initialAmount,
  optionsQuery,
  parseAmount,
  scaleIngredients,
  targetKey,
  unitLabel,
  writtenSummary,
  type ScaleMode,
} from '@/lib/recipeCard/recipeScale'
import { cn } from '@/lib/utils'

function fetchCard(t: RecipeCardTarget): Promise<RecipeCard> {
  const unwrap = (r: { data?: RecipeCard }) => {
    if (!r.data) throw new Error(RC.loadFailed)
    return r.data
  }
  switch (t.kind) {
    case 'order-item':
      return apiClient.getRecipeCardForOrderItem(t.orderItemId).then(unwrap)
    case 'product':
      return apiClient.getRecipeCardForProduct(t.productId, { order_type: t.orderType, options: optionsQuery(t.options) }).then(unwrap)
    case 'prep':
      return apiClient.getRecipeCardForPrepItem(t.prepItemId).then(unwrap)
  }
}

/** One level of the back stack: what is open and how it is scaled. */
interface Frame {
  target: RecipeCardTarget
  /** What is in the amount field; null until the card has loaded. */
  amountText: string | null
  mode: ScaleMode
}

export function RecipeCardSheet({ target, onClose }: { target: RecipeCardTarget | null; onClose: () => void }) {
  const [stack, setStack] = useState<Frame[]>([])
  // A new target from the screen starts a fresh stack.
  const rootKey = target ? targetKey(target) : null
  useEffect(() => {
    setStack(target ? [{ target, amountText: null, mode: 'base' }] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the target's identity, not its object
  }, [rootKey])

  // The stack is filled by the effect above, one render after the target
  // arrives. Until then the target itself is the card, so the sheet never
  // opens empty (and never without the title a screen reader needs).
  const frames: Frame[] = stack.length > 0 ? stack : target ? [{ target, amountText: null, mode: 'base' }] : []
  const frame = frames[frames.length - 1]
  const parent = frames.length > 1 ? frames[frames.length - 2] : null

  const patchTop = (patch: Partial<Frame>) =>
    setStack((s) => {
      const base = s.length > 0 ? s : target ? [{ target, amountText: null, mode: 'base' as ScaleMode }] : s
      return base.length === 0 ? base : [...base.slice(0, -1), { ...base[base.length - 1], ...patch }]
    })

  return (
    <Sheet open={target != null} onOpenChange={(open) => { if (!open) onClose() }}>
      {/* z-[70]: on a narrow screen the app's own top bar is z-[60] and would
          cover this sheet's title, Back and Close — the only way out when the
          sheet is full width. */}
      <SheetContent size="lg" className="z-[70]" aria-describedby={undefined}>
        {frame && (
          <CardFrame
            key={`${frames.length}:${targetKey(frame.target)}`}
            frame={frame}
            parentName={parent ? (parent.target.name ?? '') : null}
            onBack={() => setStack((s) => s.slice(0, -1))}
            onPatch={patchTop}
            onOpenPrep={(prepItemId, name, qty) =>
              setStack((s) => [...(s.length > 0 ? s : frames), { target: { kind: 'prep', prepItemId, name, qty }, amountText: null, mode: 'base' }])
            }
            onNamed={(name) => {
              if (frame.target.name !== name) patchTop({ target: { ...frame.target, name } })
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}

function CardFrame({ frame, parentName, onBack, onPatch, onOpenPrep, onNamed }: {
  frame: Frame
  parentName: string | null
  onBack: () => void
  onPatch: (patch: Partial<Frame>) => void
  onOpenPrep: (prepItemId: string, name: string, qty: number) => void
  onNamed: (name: string) => void
}) {
  const { target } = frame
  const { data: card, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: [RECIPE_CARD_QK, 'card', targetKey(target)],
    queryFn: () => fetchCard(target),
    // A recipe does not change mid-service; the board refetching around the
    // open card must not make it flicker or reset.
    staleTime: 60_000,
    retry: false,
  })

  // Seed the amount once, when the card first arrives.
  useEffect(() => {
    if (!card) return
    onNamed(card.name)
    if (frame.amountText == null) onPatch({ amountText: String(initialAmount(card, target)), mode: 'base' })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the card arrives
  }, [card])

  const title = card?.name ?? target.name ?? RC.open
  const code = (error as { code?: string } | null)?.code

  return (
    <>
      <SheetHeader className="pr-12">
        {parentName != null && (
          <Button type="button" variant="ghost" size="sm" className="-ml-2 w-fit gap-1 text-muted-foreground" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {parentName ? RC.backTo(parentName) : RC.back}
          </Button>
        )}
        <SheetTitle className="flex items-center gap-2">
          {card?.kind === 'prep' && <ChefHat className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />}
          <span className="min-w-0 break-words">{title}</span>
        </SheetTitle>
        {card && card.options.length > 0 && (
          <SheetDescription>
            {RC.options} {card.options.join(', ')}
          </SheetDescription>
        )}
      </SheetHeader>
      <SheetBody className="space-y-5">
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {RC.loading}
          </p>
        ) : error || !card ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {code === 'recipe_card_not_found' ? RC.notFound : code === 'recipe_cards_off' ? RC.switchedOff : RC.loadFailed}
            </p>
            {code === 'recipe_card_not_found' ? (
              <p className="text-sm text-muted-foreground">{RC.notFoundHelp}</p>
            ) : code === 'recipe_cards_off' ? null : (
              <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => void refetch()}>
                {RC.tryAgain}
              </Button>
            )}
          </div>
        ) : (
          <CardBody card={card} frame={frame} onPatch={onPatch} onOpenPrep={onOpenPrep} />
        )}
      </SheetBody>
    </>
  )
}

function CardBody({ card, frame, onPatch, onOpenPrep }: {
  card: RecipeCard
  frame: Frame
  onPatch: (patch: Partial<Frame>) => void
  onOpenPrep: (prepItemId: string, name: string, qty: number) => void
}) {
  const { mode, target } = frame
  const amountText = frame.amountText ?? String(initialAmount(card, target))
  const amount = parseAmount(amountText)
  const factor = amount == null ? 0 : factorFor(card, amount, mode)
  const lines = useMemo(() => scaleIngredients(card, factor), [card, factor])
  const byWeight = canScaleByWeight(card)
  const unit = amountUnit(card, mode)

  const setMode = (next: ScaleMode) => {
    if (next === mode) return
    const n = amount == null ? null : amountInMode(card, amount, mode, next)
    onPatch({ mode: next, amountText: n == null ? amountText : String(n) })
  }

  // Where the card was opened from, offered as a chip so a cook who has
  // changed the amount can get back to it in one tap.
  const opened = initialAmount(card, target)
  const openedInMode = amountInMode(card, opened, 'base', mode)
  const openedLabel =
    card.line_qty != null
      ? RC.thisOrder(formatAmount(openedInMode, unit))
      : 'qty' in target && target.qty != null && target.qty > 0
        ? RC.planned(formatAmount(openedInMode, unit))
        : null

  return (
    <>
      <section aria-label={RC.amountLabel} className="space-y-3 rounded-lg border bg-muted/30 p-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <label className="block">
            <span className="text-sm font-medium">{RC.amountLabel}</span>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                className="w-28 tabular-nums"
                value={amountText}
                onChange={(e) => onPatch({ amountText: e.target.value })}
                onFocus={(e) => e.currentTarget.select()}
                aria-invalid={amount == null}
              />
              <span className="text-sm text-muted-foreground">{unitLabel(unit, amount ?? 2)}</span>
            </div>
          </label>
          {byWeight && (
            <div role="group" aria-label={RC.making} className="inline-flex rounded-lg border bg-background p-0.5">
              {(['base', 'weight'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {m === 'base' ? RC.byPortions : RC.byWeight(card.portion_unit)}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {openedLabel && (
            <Button type="button" variant={amount === openedInMode ? 'default' : 'outline'} size="sm" onClick={() => onPatch({ amountText: String(openedInMode) })}>
              {openedLabel}
            </Button>
          )}
          {SCALE_CHIPS.map((n) => {
            const a = chipAmount(card, mode, n)
            return (
              <Button
                key={n}
                type="button"
                variant={amount === a ? 'default' : 'outline'}
                size="sm"
                className="tabular-nums"
                title={RC.chipHelp(n, formatAmount(a, unit))}
                aria-label={RC.chipHelp(n, formatAmount(a, unit))}
                onClick={() => onPatch({ amountText: String(a) })}
              >
                {RC.chip(n)}
              </Button>
            )
          })}
        </div>
        {amount == null ? (
          <p role="alert" className="text-sm text-destructive">{RC.badAmount}</p>
        ) : (
          <p className="text-sm">
            <span className="text-muted-foreground">{RC.making} </span>
            <span className="font-semibold tabular-nums">{batchSummary(card, factor)}</span>
          </p>
        )}
        <p className="text-xs text-muted-foreground">{RC.writtenFor(writtenSummary(card))}</p>
      </section>

      <section aria-labelledby="rc-ingredients">
        <h3 id="rc-ingredients" className="mb-2 text-sm font-semibold">{RC.ingredients}</h3>
        {lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">{RC.noIngredients}</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {lines.map((l) => {
              const row = (
                <>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm font-medium">{l.name}</span>
                    {(l.has_card || l.from.length > 0) && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {[l.has_card ? RC.madeHere : '', l.from.length > 0 ? RC.from(l.from) : ''].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-base font-semibold tabular-nums">{l.text}</span>
                  {l.has_card && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
                </>
              )
              return (
                <li key={l.stock_item_id}>
                  {l.has_card ? (
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted"
                      aria-label={RC.openPrep(l.name, formatQty(l.prepQty, l.prepUnit))}
                      onClick={() => onOpenPrep(l.stock_item_id, l.name, l.prepQty)}
                    >
                      {row}
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 px-3 py-2.5">{row}</div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section aria-labelledby="rc-method">
        <h3 id="rc-method" className="mb-2 text-sm font-semibold">{RC.method}</h3>
        {card.steps.length === 0 ? (
          <p className="text-sm text-muted-foreground">{RC.noMethod}</p>
        ) : (
          <ol className="space-y-3">
            {card.steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums" aria-hidden>
                  {i + 1}
                </span>
                <p className="min-w-0 flex-1 whitespace-pre-line break-words text-sm leading-relaxed">
                  <span className="sr-only">{RC.stepLabel(i + 1)}: </span>
                  {s}
                </p>
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  )
}

/** An amount in the amount field's unit: "40 portions", "10 kg". Never re-united. */
function formatAmount(n: number, unit: string): string {
  return `${n} ${unitLabel(unit, n)}`
}
